import React, { useMemo } from 'react';

import {
  Select,
  FormLabel,
  FormControl,
  Stack,
  Skeleton,
  Box,
  Heading,
  Icon,
  Text,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  PopoverCloseButton,
  useColorModeValue,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { FiExternalLink, FiHelpCircle } from 'react-icons/fi';

import { LciaMethod } from 'hooks/data/datasets/lcia/types';
import useSavedLciaMethod from 'hooks/useSavedLciaMethod';
import { useDatasetSession } from 'pages/Dataset/DatasetSessionContext';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import ImpactAssessmentTable from './components/ImpactAssessmentTable';
import IPCC2021Alert from './components/IPCC2021Alert';
import IPCCAlert from './components/IPCCAlert';
import { ipccBiogenicsMethodNames } from './constants';

type Props = { methodList?: LciaMethod[]; spoldId: number; isLoading?: boolean; hideScores?: boolean };

const ImpactAssessmentDetails = ({ methodList, spoldId, isLoading, hideScores }: Props) => {
  const { selectedMethod, setSelectedMethod } = useSavedLciaMethod(methodList);
  const { increment } = useDatasetSession();
  const iconColor = useColorModeValue('green.500', 'green.300');
  const linkIconColor = useColorModeValue('green.500', 'green.200');

  const methodMap = useMemo(() => {
    if (!methodList) return {};

    return methodList.reduce(
      (acc, m) => {
        acc[m.method_id] = m;
        return acc;
      },
      {} as Record<number, LciaMethod>
    );
  }, [methodList]);

  return (
    <Stack spacing={4}>
      {ipccBiogenicsMethodNames.includes(selectedMethod?.method_name || '') && <IPCCAlert />}
      {selectedMethod?.method_name === 'IPCC 2021' && <IPCC2021Alert />}
      <FormControl size="sm" data-tour-target="lcia-method-selector">
        <Skeleton isLoaded={!isLoading} alignSelf={'flex-start'}>
          <FormLabel id="method" fontSize={'sm'}>
            LCIA Method{' '}
            <Popover
              onOpen={() => {
                mixpanel.track(MIXPANEL_EVENTS.VIEW_LCIA_METHOD_INFO_CLICKED);
              }}
              onClose={() => {
                mixpanel.track(MIXPANEL_EVENTS.VIEW_LCIA_METHOD_INFO_CLOSED);
              }}
              strategy="fixed"
            >
              <PopoverTrigger>
                <Box as="span" verticalAlign={'middle'} ml={2} display={'inline-block'}>
                  <Icon mt="2px" as={FiHelpCircle} color={iconColor} />
                </Box>
              </PopoverTrigger>
              <PopoverContent maxW="420px" w="auto" px={6} py={5}>
                <PopoverArrow />
                <PopoverCloseButton top={4} right={6} />
                <PopoverHeader borderBottom="none" p={0}>
                  <Heading size="sm">Choose an LCIA method</Heading>
                </PopoverHeader>
                <PopoverBody p={0} mt={4}>
                  <Text>
                    Different methods use different scientific models to calculate environmental impacts. Some focus on a single
                    impact category like climate change (
                    <Link href="https://support.ecoinvent.org/guidance-on-ipcc-methods" isExternal color={iconColor}>
                      IPCC
                    </Link>
                    ), while others cover multiple categories, such as toxicity, water use, and land use (
                    <Link href="https://www.rivm.nl/en/life-cycle-assessment-lca/recipe" isExternal color={iconColor}>
                      ReCiPe
                    </Link>
                    ,{' '}
                    <Link href="https://eplca.jrc.ec.europa.eu/LCDN/EF_archive.html" isExternal color={iconColor}>
                      EF
                    </Link>
                    ).
                  </Text>
                  <Text mb={2}>Commonly used LCIA methods:</Text>
                  <Box as="ul" pl={6}>
                    <Box as="li">
                      <Text>For carbon accounting: IPCC 2021</Text>
                    </Box>
                    <Box as="li">
                      <Text>For comprehensive environmental impact assessment: EF v3.1 or ReCiPe 2016</Text>
                    </Box>
                    <Box as="li">
                      <Text>For specific regional requirements: check which method your industry or regulation requires</Text>
                    </Box>
                  </Box>
                  <Link
                    href="https://support.ecoinvent.org/impact-assessment"
                    isExternal
                    display="flex"
                    alignItems="center"
                    gap={1}
                    pt={4}
                    size="sm"
                    onClick={() => {
                      mixpanel.track(MIXPANEL_EVENTS.LCIA_METHOD_INFO_LINK_CLICKED);
                    }}
                  >
                    <Icon as={FiExternalLink} color={linkIconColor} />
                    Learn more about each available method
                  </Link>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </FormLabel>
        </Skeleton>
        <Skeleton isLoaded={!isLoading}>
          <Select
            isDisabled={!methodList?.length}
            value={selectedMethod?.method_id}
            onChange={(e) => {
              setSelectedMethod(methodMap[parseInt(e.target.value)]);
              mixpanel.track(MIXPANEL_EVENTS.IMPACT_ASSESSMENT_METHOD_SELECTED, {
                spold_id: spoldId,
                lcia_method: methodMap[parseInt(e.target.value)]?.method_name,
              });
              increment('impact_assessment_action_count');
            }}
          >
            {methodList?.map((m) => {
              return (
                <option key={m.method_id} value={m.method_id}>
                  {m.method_name}
                </option>
              );
            })}
          </Select>
        </Skeleton>
      </FormControl>
      {selectedMethod && (
        <ImpactAssessmentTable
          selectedMethod={selectedMethod}
          spoldId={spoldId}
          isLoading={isLoading}
          hideScores={hideScores}
        />
      )}
    </Stack>
  );
};

export default ImpactAssessmentDetails;
